import TrainingCourseSelect from "../training/TrainingCourseSelect";
import React, { useState, useEffect } from "react";
import DropdownNew, { DropdownOption } from "./Dropdown";
import Input from "./Input";
import { useAuth } from "../../../firebase/AuthProvider";
import { getInterimTemplates } from "../../../services/taskServices/TaskService";
import InterimTemplateResponse from "../../../interfaces/task/InterimTemplateResponse";
import { publishTemplateInterimTask, publishTask } from "../../../services/taskServices/TaskService";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import NewTask from "../../../interfaces/task/NewTask";
import { getTaskLKValues } from "../../../services/taskServices/TaskLKService";
import TaskCreationLKValues from "../../../interfaces/lkValues/TaskCreationLKValues";
import { GetAssignableParticipantsAsync } from "../../../services/participantServices/ParticipantService";
import { useNotifications } from "../../../contexts/NotificationProvider";

import { X, CheckSquare, Calendar, Info } from "lucide-react";

import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { getDisplayErrorMessage } from "../../../utilities/ErrorMessages/getErrorMessage";
import { calculateInterimTemplateDueDate } from "./interimTemplateDate";
import "./NewTask.css";

const WAIVER_BADGE_COLORS = [
    "bg-blue-50 text-blue-700 ring-blue-600/20",
    "bg-emerald-50 text-emerald-700 ring-emerald-600/20",
    "bg-indigo-50 text-indigo-700 ring-indigo-600/20",
    "bg-amber-50 text-amber-700 ring-amber-600/20",
    "bg-fuchsia-50 text-fuchsia-700 ring-fuchsia-600/20",
    "bg-cyan-50 text-cyan-700 ring-cyan-600/20",
    "bg-violet-50 text-violet-700 ring-violet-600/20",
    "bg-rose-50 text-rose-700 ring-rose-600/20",
    "bg-teal-50 text-teal-700 ring-teal-600/20",
    "bg-orange-50 text-orange-700 ring-orange-600/20",
];

const getWaiverFamilyDisplayName = (label: string) =>
    label.split("-")[0].trim();

const getStableHash = (value: string) => {
    let hash = 0;

    for (let i = 0; i < value.length; i++) {
        hash = (hash << 5) - hash + value.charCodeAt(i);
        hash |= 0;
    }

    return Math.abs(hash);
};

const buildWaiverBadgeColorMap = (familyNames: string[]) => {
    const uniqueWaiverFamilies = Array.from(
        new Set(
            familyNames
                .map(getWaiverFamilyDisplayName)
                .filter(Boolean),
        ),
    ).sort((a, b) => a.localeCompare(b));

    const usedColorIndexes = new Set<number>();

    return uniqueWaiverFamilies.reduce<Record<string, string>>((acc, name) => {
        const normalizedName = name.trim().toLowerCase();
        const preferredIndex =
            getStableHash(normalizedName) % WAIVER_BADGE_COLORS.length;

        let selectedIndex = preferredIndex;

        if (usedColorIndexes.size < WAIVER_BADGE_COLORS.length) {
            for (let offset = 0; offset < WAIVER_BADGE_COLORS.length; offset++) {
                const candidateIndex =
                    (preferredIndex + offset) % WAIVER_BADGE_COLORS.length;

                if (!usedColorIndexes.has(candidateIndex)) {
                    selectedIndex = candidateIndex;
                    break;
                }
            }

            usedColorIndexes.add(selectedIndex);
        }

        acc[name] = WAIVER_BADGE_COLORS[selectedIndex];
        return acc;
    }, {});
};

const getTemplateWaiverFamilies = (
    template: InterimTemplateResponse,
): string[] => {
    const rawFamilies: any[] =
        (template as any).waiverFamilies ??
        (template as any).waivers ??
        [];

    const familyNames = rawFamilies
        .map((waiver: any): string =>
            getWaiverFamilyDisplayName(
                String(
                    waiver?.familyCode ??
                    waiver?.waiverFamilyCode ??
                    waiver?.code ??
                    waiver?.waiverName ??
                    "",
                ),
            ),
        )
        .filter((family): family is string => family.length > 0);

    return Array.from(new Set<string>(familyNames)).sort((a, b) =>
        a.localeCompare(b),
    );
};

interface LogActivityModalProps {
    onClose: () => void;
    initialTaskContext?: "Client" | "Employee";
}

export default function NewTaskComponent({ onClose, initialTaskContext = "Client" }: LogActivityModalProps) {
    const { currentUser, currentOrganization, currentRoles, currentUserInfo } = useAuth();
    const navigate = useNavigate();
    const { notify } = useNotifications();

    const [templates, setTemplates] = useState<InterimTemplateResponse[]>([]);
    const [templateOptions, setTemplateOptions] = useState<DropdownOption[]>([]);
    const [selectedTemplateId, setSelectedTemplateId] = useState<string | number>("");

    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [assignee, setAssignee] = useState<string | number>("");
    const [employeeSubject, setEmployeeSubject] = useState<string | number>("");
    const [participant, setParticipant] = useState<string | number>("");
    const [trainingCourseId, setTrainingCourseId] = useState<number | null>(null);
    const [taskContext, setTaskContext] = useState<"Client" | "Employee">(initialTaskContext);
    useEffect(() => { if (taskContext === "Employee" && trainingCourseId && employeeSubject) setAssignee(employeeSubject); }, [taskContext, trainingCourseId, employeeSubject]);
    const [dueDate, setDueDate] = useState<Date | null>(null);

    const [lkOptions, setLkOptions] = useState<TaskCreationLKValues | null>(null);
    const [, setTaskOrganizationOptions] = useState<DropdownOption[]>([]);
    const [, setTaskTypeOptions] = useState<DropdownOption[]>([]);
    const [, setTaskSubTypeOptions] = useState<DropdownOption[]>([]);
    const [assigneeOptions, setAssigneeOptions] = useState<DropdownOption[]>([]);
    const [employeeSubjectOptions, setEmployeeSubjectOptions] = useState<DropdownOption[]>([]);
    const [participantOptions, setParticipantOptions] = useState<DropdownOption[]>([]);

    const [isSC, setIsSC] = useState<boolean>(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [loading, setLoading] = useState<{ participant: boolean; assignee: boolean }>({
        participant: false,
        assignee: false
    });

    const [errors, setErrors] = useState<Record<string, string>>({});

    useEffect(() => {
        const fetchData = async () => {
            if (currentUser) {
                try {
                    const authToken = await currentUser.getIdToken();
                    const templateResponse = await getInterimTemplates(authToken);
                    if (templateResponse) {
                        setTemplates(templateResponse);
                    }

                    const lkResponse = await getTaskLKValues(authToken);
                    if (lkResponse) {
                        setLkOptions(lkResponse);
                    }
                } catch (error) {
                    toast.error("Failed to load data");
                }
            }
        };

        fetchData();
    }, [currentUser]);

    useEffect(() => {
        const matchingTemplates = templates.filter((template) => (template.taskContext ?? "Client") === taskContext);
        const allWaiverFamilies = matchingTemplates.flatMap(getTemplateWaiverFamilies);
        const waiverBadgeColorMap = buildWaiverBadgeColorMap(allWaiverFamilies);
        setTemplateOptions(matchingTemplates.map((template) => {
            const waiverFamilies = getTemplateWaiverFamilies(template);
            const employeeBadges = [{
                label: template.visibilityMode === "Restricted" ? "Confidential Employee Work" : "Employee Work",
                className: template.visibilityMode === "Restricted"
                    ? "bg-rose-50 text-rose-700 ring-rose-600/20"
                    : "bg-purple-50 text-purple-700 ring-purple-600/20",
            }];
            return {
                label: template.title,
                value: template.id,
                searchText: taskContext === "Employee" ? "employee work" : waiverFamilies.join(" "),
                badges: taskContext === "Employee"
                    ? employeeBadges
                    : waiverFamilies.length > 0
                        ? waiverFamilies.map((family) => ({
                            label: family,
                            className: waiverBadgeColorMap[family] ?? "bg-gray-50 text-gray-700 ring-gray-600/20",
                        }))
                        : [{ label: "All Waivers", className: "bg-gray-50 text-gray-600 ring-gray-500/20" }],
            };
        }));
    }, [templates, taskContext]);

    useEffect(() => {
        if (lkOptions && currentUser) {
            const isSCHighestRole = currentRoles?.length === 1 && currentRoles[0].name === "SC";
            setIsSC(isSCHighestRole);

            if (isSCHighestRole && currentUser) {
                setAssignee(currentUser.uid);
            }

            const formattedTaskOrganizations = lkOptions.taskOrganizations.map(org => ({ value: org.id.toString(), label: org.type }));
            const formattedTaskTypes = lkOptions.taskTypes.map(tt => ({ value: tt.id.toString(), label: tt.type }));
            const formattedTaskSubTypes = lkOptions.taskSubTypes.map(tst => ({ value: tst.id.toString(), label: tst.type }));
            const employeeOptions = lkOptions.users.map(user => ({
                value: user.id.toString(),
                label: user.displayName,
            }));
            setEmployeeSubjectOptions(employeeOptions);

            setTaskOrganizationOptions(formattedTaskOrganizations);
            setTaskTypeOptions(formattedTaskTypes);
            setTaskSubTypeOptions(formattedTaskSubTypes);

            if (!isSCHighestRole) {
                const assignedToYouOption = [{
                    value: currentUser?.uid,
                    label: currentUser?.displayName ? `Assign to ${currentUser.displayName}` : 'Assign to yourself',
                }];

                const assignelkoption = lkOptions.users.map(user => ({
                    value: user.id.toString(),
                    label: user.displayName,
                })).filter(u => u.value !== currentUser.uid);

                setAssigneeOptions([...assignedToYouOption, ...assignelkoption]);
            } else {
                setAssigneeOptions([]);
            }
        }
    }, [lkOptions, currentUser, currentRoles]);


    useEffect(() => {
        if (selectedTemplateId && templates.length > 0) {
            const template = templates.find(t => t.id === selectedTemplateId);
            if (template) {
                setTitle(template.title);
                setDescription(template.description);
                setTaskContext(template.taskContext ?? "Client");
                setTrainingCourseId(template.taskContext === "Employee" ? template.trainingCourseId ?? null : null);
                if (template.daysFromCreationDueDate != null) {
                    setDueDate(calculateInterimTemplateDueDate(template.daysFromCreationDueDate));
                }
            }
        } else {
            setTitle("");
            setDescription("");
            setDueDate(null);
        }
    }, [selectedTemplateId, templates]);

    useEffect(() => {
      let cancelled = false;
      const getAllParticipantUnderMe = async () => {
        if (!currentUser || !assignee) {
            setParticipantOptions([]);
            setParticipant("");
            setLoading(prev => ({ ...prev, participant: false }));
            return;
        }

        try {
            setLoading(prev => ({ ...prev, participant: true }));
            setParticipantOptions([]);
            setParticipant("");

            const token = await currentUser.getIdToken();
            // const res = await GetAllParticipantUnderMeAsync(token, assignee.toString());
            const res = await GetAssignableParticipantsAsync(token, assignee.toString());

            if (!cancelled && res.success && res.data) {
                const formattedParticipants = res.data.map(part => ({
                    value: part.id.toString(),
                    label: part.firstName + " " + part.lastName,
                }));
                setParticipantOptions(formattedParticipants);
            }
        } catch (error) {
            if (!cancelled) console.error("Error fetching participants:", error);
        } finally {
            if (!cancelled) setLoading(prev => ({ ...prev, participant: false }));
        }
      };
      getAllParticipantUnderMe();
      return () => { cancelled = true; };
    }, [currentUser, assignee]);

    const handleTemplateChange = (value: string | number) => {
        setSelectedTemplateId(value);
        if (errors.template) {
            setErrors(prev => { const newErrors = { ...prev }; delete newErrors.template; return newErrors; });
        }
    };

    const validateField = (name: string, value: any) => {
        let errorMessage = "";
        switch (name) {
            case "title":
                if (!value || value.trim() === "") errorMessage = "Title is required.";
                break;
            case "description":
                if (!value || value.trim() === "") errorMessage = "Description is required.";
                break;
            case "dueDate":
                if (!value) errorMessage = "Due date is required.";
                break;
            case "assignee":
                if (!value && !isSC) errorMessage = "Assignee is required.";
                break;
            case "participant":
                if (taskContext === "Client" && !value) errorMessage = "Participant is required.";
                break;
            case "employeeSubject":
                if (taskContext === "Employee" && !value) errorMessage = "Employee subject is required.";
                break;
        }
        return errorMessage;
    };

    const validateForm = () => {
        const newErrors: Record<string, string> = {};
        const validations = [
            validateField("title", title),
            validateField("description", description),
            validateField("dueDate", dueDate),
            validateField("assignee", assignee),
            validateField("participant", participant),
            validateField("employeeSubject", employeeSubject),
        ];

        const fieldNames = ["title", "description", "dueDate", "assignee", "participant", "employeeSubject"];
        fieldNames.forEach((field, index) => {
            if (validations[index]) newErrors[field] = validations[index];
        });

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const formatDateForApi = (date: Date | null) => {
        if (!date) return "";
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            notify("Please fix validation errors before submitting", { type: 'error' });
            return;
        }

        if (!currentUser || !currentOrganization) {
            toast.error("Authentication error. Please log in again.");
            return;
        }

        setIsSubmitting(true);
        try {
            const authToken = await currentUser.getIdToken();

            const taskData: NewTask = {
                title,
                description,
                requiredActions: "",
                requiredFiles: selectedTemplateId
                    ? templates.find(t => t.id === selectedTemplateId)?.requiredFiles.map(f => f.id.toString()) || []
                    : [],
                assignee: trainingCourseId && taskContext === "Employee" ? employeeSubject.toString() : assignee.toString(),
                participant: taskContext === "Client" ? participant.toString() : undefined,
                trainingCourseId: taskContext === "Employee" ? trainingCourseId : null,
                taskContext,
                employeeSubjectId: taskContext === "Employee" ? employeeSubject.toString() : undefined,
                visibilityMode: selectedTemplateId
                    ? templates.find(t => t.id === selectedTemplateId)?.visibilityMode ?? "Standard"
                    : "Standard",
                templateTaskId: selectedTemplateId ? selectedTemplateId.toString() : undefined,
                taskOrganization: "1",
                taskType: "1",
                taskSubType: "4",
                dueDate: formatDateForApi(dueDate),
                priority: "Low" as "Low" | "Medium" | "High",
            };

            let taskFriendlyId: number | null = null;
            let orgId: number = currentUserInfo?.organization.id || 0;

            if (selectedTemplateId) {
                taskFriendlyId = await publishTemplateInterimTask(authToken, taskData, orgId);
            } else {
                const publishTaskRes = await publishTask(authToken, taskData);
                taskFriendlyId = publishTaskRes.data.friendlyId;
            }

            if (taskFriendlyId) {
                toast.success("Task created successfully!");
                navigate(`/task/${currentOrganization.abbreviation}-${taskFriendlyId}`);
                onClose();
            } else {
                toast.error("Failed to create task");
            }
        } catch (error: any) {
            const msg = getDisplayErrorMessage(error, "Failed to create task. Please try again.");
            toast.error(msg);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleInputChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const value = e.target.value;
        switch (field) {
            case "title": setTitle(value); break;
            case "description": setDescription(value); break;
        }

        if (errors[field]) {
            setErrors(prev => { const newErrors = { ...prev }; delete newErrors[field]; return newErrors; });
        }
    };

    const handleAssigneeChange = (value: string | number) => {
        setAssignee(value);
        setParticipant("");
        if (errors.assignee) setErrors(prev => { const newErrors = { ...prev }; delete newErrors.assignee; return newErrors; });
    };

    const handleParticipantChange = (value: string | number) => {
        setParticipant(value);
        if (errors.participant) setErrors(prev => { const newErrors = { ...prev }; delete newErrors.participant; return newErrors; });
    };

    const handleEmployeeSubjectChange = (value: string | number) => {
        setEmployeeSubject(value);
        if (errors.employeeSubject) setErrors(prev => { const next = { ...prev }; delete next.employeeSubject; return next; });
    };

    const isTemplateSelected = selectedTemplateId !== "";
    const clearTemplate = () => {
        setSelectedTemplateId("");
        setTrainingCourseId(null);
        setTitle("");
        setDescription("");
        setDueDate(null);
    };

    const handleTaskContextChange = (context: "Client" | "Employee") => {
        setTaskContext(context);
        setTrainingCourseId(null);
        setSelectedTemplateId("");
        setTitle("");
        setDescription("");
        setDueDate(null);
        setParticipant("");
        setEmployeeSubject("");
        setErrors({});
    };

    return (
        <>
            <style>{`
                .react-datepicker-wrapper, .react-datepicker__input-container { width: 100%; display: block; }
                .react-datepicker { border: 1px solid #E5E7EB; border-radius: 12px; font-family: 'Poppins', sans-serif; box-shadow: 0 10px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1); overflow: hidden; }
                .react-datepicker__header { background-color: #F9F7FF; border-bottom: 1px solid #E9E4FF; padding-top: 12px; }
                .react-datepicker__day--selected, .react-datepicker__day--keyboard-selected { background-color: #7E57C2 !important; color: white !important; border-radius: 8px; }
                .react-datepicker__day:hover { background-color: #F3E8FF !important; border-radius: 8px; color: #7E57C2; }
            `}</style>

            <div className="new-task-modal p-6 md:p-8 bg-white rounded-2xl">
                <div className="flex items-start justify-between mb-6 pb-4 border-b border-gray-100">
                    <div className="flex items-center gap-3">
                        <div className="flex items-center justify-center w-11 h-11 rounded-xl bg-[#F3E8FF] text-[#7E57C2] shrink-0">
                            <CheckSquare size={24} strokeWidth={2} />
                        </div>
                        <div>
                            <h2 className="text-[1.25rem] font-bold text-gray-900 m-0 leading-tight">Create New Task</h2>
                            <p className="text-[12px] font-medium text-gray-500 m-0 mt-0.5">
                                Define and assign a new workflow task
                            </p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 -mr-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors border-none bg-transparent cursor-pointer">
                        <X size={20} strokeWidth={2.5} />
                    </button>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="mb-5 inline-flex rounded-lg border border-gray-200 bg-gray-50 p-1" aria-label="Task context">
                            {(["Client", "Employee"] as const).map((context) => (
                                <button key={context} type="button" onClick={() => handleTaskContextChange(context)} className={`rounded-md px-4 py-2 text-xs font-semibold ${taskContext === context ? "bg-[#7E57C2] text-white" : "text-gray-600 hover:bg-white"}`}>
                                    {context === "Client" ? "Participant Task" : "Employee Task"}
                                </button>
                            ))}
                    </div>
                    <div className="space-y-5">

                        <div className="flex flex-col relative">
                            <DropdownNew
                                label="Select from template"
                                name="template"
                                value={selectedTemplateId}
                                onChange={handleTemplateChange}
                                options={templateOptions}
                                placeholder="Select a template to auto-fill details"
                                error={errors.template}
                            />
                            {isTemplateSelected && (
                                <button
                                    type="button"
                                    onClick={clearTemplate}
                                    className="absolute right-2 -top-2.5 px-2.5 py-1.5 text-[10px] font-bold text-[#7E57C2] bg-[#F3E8FF] hover:bg-[#E9D5FF] transition-all duration-200 uppercase tracking-widest border border-[#E9D5FF] hover:border-[#D8B4FE] rounded-md cursor-pointer shadow-sm hover:shadow-md"
                                >
                                    Clear Selection
                                </button>
                            )}
                        </div>

                        <div className="space-y-1">
                            <Input
                                label="Task Title"
                                name="title"
                                value={title}
                                placeholder="Give your task a clear, concise title..."
                                onChange={handleInputChange("title")}
                                disabled={isTemplateSelected}
                                error={errors.title}
                            />
                            {isTemplateSelected && (
                                <p className="mt-1.5 text-[10px] font-bold text-[#7E57C2] uppercase tracking-widest flex items-center gap-1.5">
                                    <Info size={12} strokeWidth={3} /> Auto-filled from template
                                </p>
                            )}
                        </div>

                        <div className="flex items-start gap-x-4">
                            <div className="w-1/2">
                                {isSC ? (
                                    <>
                                        <Input
                                            label="Assignee"
                                            name="assignee"
                                            value={currentUser?.displayName || "Assigned to you"}
                                            disabled
                                            onChange={() => { }}
                                        />
                                        <p className="mt-1.5 text-[10px] font-bold text-gray-400 uppercase tracking-widest flex items-center gap-1.5">
                                            <Info size={12} strokeWidth={3} /> Auto-Assigned
                                        </p>
                                    </>
                                ) : (
                                    <DropdownNew
                                        label="Assignee"
                                        name="assignee"
                                        value={assignee}
                                        disabled={taskContext === "Employee" && !!trainingCourseId}
                                        onChange={handleAssigneeChange}
                                        options={assigneeOptions}
                                        placeholder={loading.assignee ? "Loading..." : "Select assignee..."}
                                        error={errors.assignee}
                                    />
                                )}
                            </div>

                            <div className="w-1/2 flex flex-col">
                                <label className="block text-[14px] font-medium text-[#374151] mb-1.5">Due Date <span className="text-red-500 ml-0.5">*</span></label>
                                <div className="relative">
                                    <DatePicker
                                        calendarClassName="new-task-datepicker"
                                        popperClassName="new-task-datepicker-popper"
                                        selected={dueDate}
                                        onChange={(date: any) => {
                                            setDueDate(date);
                                            if (errors.dueDate) setErrors(prev => { const n = { ...prev }; delete n.dueDate; return n; });
                                        }}
                                        minDate={new Date()}
                                        dateFormat="MM/dd/yyyy"
                                        disabled={isTemplateSelected}
                                        /* PERFECT STYLING MATCH APPLIED HERE */
                                        className={`w-full bg-[#f9fafb] border ${errors.dueDate ? 'border-red-400' : 'border-[#e5e7eb] hover:border-[#d1d5db] focus:border-[#8b5cf6]'} text-[#111827] text-[14px] rounded-lg focus:bg-white focus:ring-0 block pl-[38px] pr-4 min-h-[48px] transition-all outline-none ${isTemplateSelected ? 'opacity-60 cursor-not-allowed' : ''}`}
                                        placeholderText="Select due date"
                                    />
                                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-[18px] h-[18px] text-[#9ca3af] pointer-events-none" />
                                </div>
                                {errors.dueDate && <span className="text-red-500 text-[12px] mt-1 font-medium">{errors.dueDate}</span>}

                                {isTemplateSelected && (
                                    <p className="mt-1.5 text-[10px] font-bold text-[#7E57C2] uppercase tracking-widest flex items-center gap-1.5">
                                        <Info size={12} strokeWidth={3} /> Auto-filled from template
                                    </p>
                                )}
                            </div>
                        </div>

                        {taskContext === "Client" && <div className="w-full">
                            <DropdownNew
                                label="Participant"
                                name="participant"
                                value={participant}
                                onChange={handleParticipantChange}
                                options={participantOptions}
                                placeholder={loading.participant ? "Loading participants..." : "Search and select a participant..."}
                                error={errors.participant}
                            />
                        </div>}

                        {taskContext === "Employee" && <div className="w-full"><div className="mb-4"><TrainingCourseSelect size="form" value={trainingCourseId} onChange={setTrainingCourseId} disabled={isTemplateSelected} employees={employeeSubject ? [{ id: employeeSubject.toString(), name: employeeSubjectOptions.find(option => option.value.toString() === employeeSubject.toString())?.label }] : undefined} /></div>
                            <DropdownNew
                                label="Employee subject"
                                name="employeeSubject"
                                value={employeeSubject}
                                onChange={handleEmployeeSubjectChange}
                                options={employeeSubjectOptions}
                                placeholder="Select the employee this work is about..."
                                error={errors.employeeSubject}
                            />
                            <p className="mt-1.5 text-[10px] font-medium text-gray-500">
                                {trainingCourseId ? "Training is assigned to this employee as the sole learner and assignee." : "The subject is separate from the assignee so supervisors or HR staff can complete work about another employee."}
                            </p>
                        </div>}

                        <div className="space-y-1">
                            <Input
                                label="Task Description"
                                type="textarea"
                                name="description"
                                value={description}
                                placeholder="A brief description of what the task entails..."
                                onChange={handleInputChange("description")}
                                error={errors.description}
                                disabled={isTemplateSelected}
                            />
                            {isTemplateSelected && (
                                <p className="mt-1.5 text-[10px] font-bold text-[#7E57C2] uppercase tracking-widest flex items-center gap-1.5">
                                    <Info size={12} strokeWidth={3} /> Auto-filled from template
                                </p>
                            )}
                        </div>

                        <div className="flex items-center justify-end gap-3 pt-5 mt-6 border-t border-gray-100">
                            <button
                                type="button"
                                onClick={onClose}
                                className="px-5 py-2.5 text-[14px] font-semibold text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 hover:text-gray-900 rounded-xl transition-colors cursor-pointer"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className="px-5 py-2.5 text-[14px] font-semibold text-white bg-[#7E57C2] hover:bg-[#6A4AA8] border-none rounded-xl transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed min-w-[130px] flex items-center justify-center"
                            >
                                {isSubmitting ? "Creating..." : "Create Task"}
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </>
    );
}
